import { SERVER_STAGES, type RunPhase, type RunState, type SearchRun, type ServerStage } from './searchRun'

/**
 * Что показывает экран хода поиска, выведенное из `SearchRun` чистыми функциями: состояния пяти крупных шагов (для разбора
 * законченного поиска) и ход: процент отправки снимка (настоящий, по байтам) и фраза этапа без процентов (см. `runProgress`).
 */
export type RunStepId = 'upload' | 'read' | 'text' | 'visual' | 'result' | 'judge'

/** Какие этапы сервера входят в крупный шаг (загрузка — на устройстве, этапов сервера у неё нет). */
export const STEP_STAGES: Readonly<Record<RunStepId, readonly ServerStage[]>> = {
  upload: [],
  read: ['read'],
  text: ['text', 'barcode'],
  visual: ['signature', 'visual'],
  result: ['merge', 'catalog'],
  judge: ['judge'],
}

const LIVE: ReadonlySet<RunPhase> = new Set(['preparing', 'uploading', 'processing'])

/** Идёт ли поиск прямо сейчас. */
export const isLive = (phase: RunPhase): boolean => LIVE.has(phase)

/**
 * Состояние шага по состояниям его частей. Пока поиск идёт, «часть готова, часть ждёт» — это «идёт»; в законченном успехом
 * поиске нетронутые части считаются пропущенными (сервер их не потребовал), в оборвавшемся — остаются «ждёт».
 */
function combine(states: readonly RunState[], phase: RunPhase): RunState {
  if (states.includes('failed')) return 'failed'
  if (states.includes('running')) return 'running'
  const done = states.filter((state) => state === 'done').length
  const skipped = states.filter((state) => state === 'skipped').length
  const untouched = states.length - done - skipped
  if (untouched === 0) return done > 0 ? 'done' : 'skipped'
  if (phase === 'done') return done > 0 ? 'done' : 'skipped'
  if (isLive(phase)) return done + skipped > 0 ? 'running' : 'pending'
  return done > 0 ? 'done' : 'pending'
}

function uploadStep(run: SearchRun): RunState {
  if (run.upload.state !== 'pending') return run.upload.state
  if (run.compress.state === 'pending') return 'pending'
  return run.compress.state === 'failed' ? 'failed' : 'running'
}

/** Состояния шагов: Загрузка → Чтение текста → Поиск по тексту → Поиск по виду → Результат → Проверка (только у спорного ответа). */
export function runStepStates(run: SearchRun): Record<RunStepId, RunState> {
  const ofStages = (id: RunStepId) => combine(STEP_STAGES[id].map((stage) => run.stages[stage].state), run.phase)
  const result = ofStages('result')
  return {
    upload: uploadStep(run),
    read: ofStages('read'),
    // «Подготовка поиска» (ждали холодную службу текста) идёт внутри шага «Поиск по тексту».
    text: run.stages.prepare.state === 'running' ? 'running' : ofStages('text'),
    visual: ofStages('visual'),
    // «Результат» готов, когда поиск закончился успехом, даже если сервер не прислал событий слияния.
    result: run.phase === 'done' ? 'done' : result,
    judge: ofStages('judge'),
  }
}

/** «Обычно» этапа, если сервер замеров ещё не накопил (мс): грубая прикидка, только чтобы подпись «Ещё немного…» не появлялась сразу. */
const FALLBACK_TYPICAL_MS: Readonly<Record<ServerStage, number>> = {
  read: 1500,
  signature: 12000,
  visual: 1200,
  barcode: 300,
  text: 1500,
  merge: 200,
  catalog: 300,
  judge: 30000,
  prepare: 1000,
}

export interface RunProgress {
  /**
   * Целые проценты 0–100 ТОЛЬКО там, где процент настоящий: отправка снимка считается по реальным байтам, законченный поиск — 100. Пока сервер ищет,
   * процента нет (`null`): у поиска нет общего знаменателя, и выдумывать «99 %» нельзя — полоса показывает, что работа идёт, а фраза — что именно делается.
   */
  percent: number | null
  /** Идущий этап вышел за обычное время: подпись «Ещё немного…», без секунд. */
  stalled: boolean
  /** Короткая фраза этапа без цифр времени. */
  phrase: string
}

/**
 * Ход поиска (решение владельца 02.11.2026): сначала загрузка снимка до настоящих 100 % по байтам, затем фразы этапов сервера без процентов.
 * `now` нужен только чтобы понять, что идущий этап дольше обычного («обычно» из плана сервера), на экран время не выводится.
 */
export function runProgress(run: SearchRun, now: number): RunProgress {
  const phrase = progressPhrase(run)
  if (run.phase === 'done') return { percent: 100, stalled: false, phrase }
  let stalled = false
  for (const name of SERVER_STAGES) {
    const stage = run.stages[name]
    if (stage.state !== 'running' || stage.startedAt === null) continue
    if (Math.max(0, now - stage.startedAt) > Math.max(1, stage.typicalMs ?? FALLBACK_TYPICAL_MS[name])) stalled = true
  }
  if (run.phase === 'preparing' || run.phase === 'uploading') {
    const sent = uploadFraction(run)
    return { percent: run.upload.state === 'done' ? 100 : sent === null ? 0 : Math.floor(sent * 100), stalled, phrase }
  }
  return { percent: null, stalled, phrase }
}

/** Фраза под полосой: что делается сейчас, без времени и без процентов, которых нет. */
function progressPhrase(run: SearchRun): string {
  switch (run.phase) {
    case 'idle':
      return ''
    case 'preparing':
    case 'uploading':
      return 'Отправляем снимок'
    case 'processing':
      if (run.stages.prepare.state === 'running') return 'Готовим поиск'
      if (run.stages.judge.state === 'running') return 'ИИ сравнивает кандидатов'
      if (run.stages.signature.state === 'running') return 'Убираем фон'
      if (run.stages.visual.state === 'running') return 'Ищем похожие'
      if (run.stages.merge.state === 'running' || run.stages.catalog.state === 'running') return 'Собираем выдачу'
      return run.stages.read.state === 'pending' || run.stages.read.state === 'running' ? 'Читаем текст' : 'Ищем товар'
    case 'done':
      return 'Готово'
    case 'failed':
      return 'Не получилось'
    case 'cancelled':
      return 'Поиск отменён'
  }
}

/** Доля загруженного снимка 0..1 по настоящим байтам; `null` — размер неизвестен. */
export function uploadFraction(run: SearchRun): number | null {
  const { loaded, total } = run.upload
  if (total === null || total <= 0) return null
  return Math.min(1, Math.max(0, loaded / total))
}
